import React from "react";
import { useTranslation } from "react-i18next";
import {
  useAuthentication,
  useRetrieveCurrentPlanzone,
  useRetrievePlanzoneResourcesCounts,
} from "@planzone/view-models";
import { Identifiable, Resource as R, ResourceTypes } from "@planzone/dto";

import { UserIcon } from "@common/assets/userIcon";
import { ResourceAvatar } from "@common/resourceAvatar";
import { PagedResources } from "@common/pagedResources";
import { Loader as Spinner } from "@common/loader";
import { Loader } from "../loader";
import { Checkbox } from "../checkbox";
import { Section } from "../section";
import {
  filterSearch,
  useAllPlanzoneResources,
  useProjectMembers,
} from "../utils";
import { Props } from "./types";
import { Resource } from "./resource";
import styles from "./styles.scss";

function Resources({
  search,
  allocatedResources,
  style,
  select,
  unSelect,
  isOpen,
  everyone: everyoneByBack,
}: Props) {
  const { t } = useTranslation();
  const { account } = useAuthentication();
  const { isInProject, isRequestPending, members } = useProjectMembers();
  const planzoneResources = useAllPlanzoneResources();
  const { planzone } = useRetrieveCurrentPlanzone();
  const {
    counts,
    isRequestPending: isCountsRequestPending,
    isRequestSuccess: isCountsRequestSuccess,
    isRequestFailure: isCountsRequestFailure,
    retrievePlanzoneResourcesCounts,
  } = useRetrievePlanzoneResourcesCounts();
  // Counted by the back outside a project; in it, the members are loaded and tell who is in.
  const isCountedByBack = !isInProject && !!everyoneByBack;
  const isProjectMember =
    !isInProject ||
    !!members.find(({ uuid }) => uuid === account.resource.uuid);

  const isSelected = (resource: Identifiable<R>) =>
    !!allocatedResources.find(({ uuid }) => resource.uuid === uuid);
  const selectResources = (resources: Identifiable<R>[]) =>
    select(resources.filter((resource) => !isSelected(resource)));
  const unSelectResources = unSelect;
  // Outside a project, everyone is only known once the whole list has been loaded to allocate it.
  const everyone = isInProject
    ? members
    : planzoneResources.isLoaded
      ? planzoneResources.resources
      : [];
  // Added by the back, everyone is only known by the count of the activated planzone resources,
  // also checked when already all allocated.
  const excludedResource = everyoneByBack?.excludedResource;
  const activatedCount =
    counts.human + counts.material + counts.virtual - counts.inactive;
  const everyoneCount =
    activatedCount -
    (excludedResource && !excludedResource.deactivated ? 1 : 0);
  const isEveryoneSelected = !isCountedByBack
    ? everyone.length > 0 && everyone.every((r) => isSelected(r))
    : everyoneByBack.isSelected ||
      (everyoneCount > 0 &&
        allocatedResources.filter(
          (resource) =>
            !resource.deactivated && resource.uuid !== excludedResource?.uuid,
        ).length >= everyoneCount);

  React.useEffect(() => {
    isCountedByBack &&
      !isCountsRequestPending &&
      !isCountsRequestSuccess &&
      !isCountsRequestFailure &&
      retrievePlanzoneResourcesCounts({ planzone });
  }, [isCountedByBack]);

  const activatedResources = (resources: Identifiable<R>[]) =>
    resources.filter((resource) => !resource.deactivated);
  const renderResources = (resources: Identifiable<R>[]) =>
    activatedResources(resources).map((resource) => (
      <Resource
        key={resource.uuid}
        resource={resource}
        isSelected={isSelected(resource)}
        select={() => selectResources([resource])}
        unSelect={() => unSelectResources([resource])}
      />
    ));

  return (
    <div className={styles.resources}>
      <div
        className={styles.row}
        onClick={() =>
          isCountedByBack
            ? isEveryoneSelected
              ? everyoneByBack.unSelect?.()
              : !everyoneByBack.isPending && everyoneByBack.select()
            : isEveryoneSelected
              ? unSelectResources(everyone)
              : everyoneByBack
                ? !everyoneByBack.isPending && everyoneByBack.select()
                : isInProject
                  ? selectResources(members)
                  : planzoneResources.withAllResources(selectResources)
        }
      >
        <Checkbox isChecked={isEveryoneSelected} />
        <p className={styles.title}>
          {t("common.filterMenu.resources.everyone")}
        </p>
        {(planzoneResources.isRequestPending || everyoneByBack?.isPending) && (
          <div className={styles.spinner}>
            <Spinner />
          </div>
        )}
      </div>
      {isProjectMember && (
        <div
          className={styles.row}
          onClick={() =>
            isSelected(account.resource)
              ? unSelectResources([account.resource])
              : selectResources([account.resource])
          }
        >
          <Checkbox isChecked={isSelected(account.resource)} />
          <div className={styles.avatar}>
            <ResourceAvatar resource={account.resource} />
          </div>
          <p className={styles.name}>
            {account.resource.name} ({t("common.filterMenu.resources.me")})
          </p>
        </div>
      )}
      <Section
        header={
          <div className={styles.header}>
            <div className={styles.iconBox}>
              <UserIcon className={styles.icon} />
            </div>
            <p className={styles.title}>
              {t("common.filterMenu.resources.title")}
            </p>
          </div>
        }
        list={
          isInProject ? (
            <div className={styles.list}>
              {!isRequestPending ? (
                renderResources(
                  [...filterSearch(members, search)]
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .sort((a, b) => {
                      const priority = {
                        [ResourceTypes.HUMAN]: 1,
                        [ResourceTypes.VIRTUAL]: 2,
                        [ResourceTypes.MATERIAL]: 3,
                      };

                      return priority[a.type] - priority[b.type];
                    }),
                )
              ) : (
                <Loader />
              )}
            </div>
          ) : (
            <PagedResources
              flagName="allocationResourcesFlag"
              search={search}
              isScrolledByParent
              loader={
                <div className={styles.list}>
                  <Loader />
                </div>
              }
            >
              {(resources) => (
                <div className={styles.list}>{renderResources(resources)}</div>
              )}
            </PagedResources>
          )
        }
        style={style}
        isOpen={isOpen}
      />
    </div>
  );
}

export { Resources };
